/**
 * Sticky Add to Cart Component
 *
 * Shows/hides a sticky add to cart bar at the bottom of the screen
 * based on scroll position and syncs with the main product form.
 */
class StickyAddToCartComponent extends HTMLElement {
  constructor() {
    super();
    this.threshold = 500; // Show after scrolling 500px
    this.mainProductForm = null;
    this.mainProductSection = null;
    this.mainAddToCartButton = null;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    // Get threshold from section data attribute (optional)
    /** @type {HTMLElement | null} */
    const section = this.closest('.sticky-add-to-cart-section');
    const scrollThreshold = section?.dataset?.scrollThreshold;
    if (scrollThreshold) {
      const parsed = parseInt(scrollThreshold, 10);
      if (!Number.isNaN(parsed)) this.threshold = parsed;
    }

    // Find the main product form
    this.mainProductForm = document.querySelector(
      'product-form-component[data-product-id="' + this.dataset.productId + '"]'
    );

    // Find the main product information section
    // This is the section that contains the main add-to-cart form
    this.mainProductSection = document.querySelector('.product-information');

    // Find the main "Add to cart" CTA button (desktop + mobile)
    // Prefer scoping to the main product form; fall back to product-information.
    this.mainAddToCartButton =
      this.mainProductForm?.querySelector('button[type="submit"][name="add"]') ||
      document.querySelector('.product-information button[type="submit"][name="add"]');

    // Set up scroll listener
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Listen for variant changes from the main form
    if (this.mainProductForm) {
      this.mainProductForm.addEventListener('variant:change', this.handleVariantChange);
    }

    // Initial check
    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.mainProductForm) {
      this.mainProductForm.removeEventListener('variant:change', this.handleVariantChange);
    }
  }

  /**
   * Handle scroll events to show/hide the sticky bar
   */
  handleScroll() {
    const scrollPosition = window.scrollY || window.pageYOffset;
    const pastThreshold = scrollPosition > this.threshold;

    // Hide margins: treat scrollytelling sections as "visible" only when they
    // overlap the viewport by more than a tiny amount. This prevents the sticky
    // bar from staying hidden late into the next section (e.g. The Difference).
    //
    // NOTE: Smaller values make the sticky stay hidden LONGER (tiny overlap counts).
    // Larger values make the sticky re-appear EARLIER when leaving the scrollytelling section.
    const SCROLL_SECTION_HIDE_MARGIN_PX = 80;

    // "Any overlap" check with optional offsets.
    // bottomOffset > 0 => trigger later (element must move further up into viewport)
    // bottomOffset < 0 => trigger earlier (pre-trigger before element enters viewport)
    const isElementVisible = (/** @type {Element | null} */ el, { topOffset = 0, bottomOffset = 0 } = {}) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight - bottomOffset && rect.bottom > topOffset;
    };

    // Check if we've scrolled past an element's position
    // Returns true if the element's top position is above the viewport top
    const hasScrolledPastElement = (/** @type {Element | null} */ el) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      // Element's top is above viewport top (we've scrolled past it)
      return rect.top < 0;
    };

    // Check if we've scrolled past a tall scrollytelling section
    // For very tall sections (like image-sequence-canvas at 240vh), we need to detect
    // when we're past the active animation area, not just when the section is visible
    const isScrollSectionActive = (/** @type {Element | null} */ el) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const sectionHeight = rect.height;
      
      // If section is very tall (more than 2 viewport heights), use special logic
      // For image-sequence-canvas (240vh), we need to detect when we've scrolled past the active animation
      if (sectionHeight > viewportHeight * 2) {
        const sectionTop = rect.top;
        const sectionBottom = rect.bottom;
        
        // For tall sticky sections, check if we're still actively in the section or past it.
        // The section uses sticky positioning, so the top might stay near 0 while scrolling.
        // We need to check both top and bottom positions to determine if we're past it.
        
        // Keep sticky hidden if:
        // 1. Section top is near or above viewport top (within 200px) - we're still in it
        // 2. AND section bottom is still significantly in viewport (above 40% from bottom)
        
        // Show sticky bar if:
        // 1. Section bottom is above 40% of viewport (we've scrolled through most of it)
        // 2. OR section top is well below viewport top (we've clearly scrolled past it)
        
        const topThreshold = 200; // pixels - if top is within 200px of viewport top, still in section
        const bottomThreshold = viewportHeight * 0.4; // 40% of viewport
        
        // If section top is well below viewport top, we've scrolled past it
        if (sectionTop > topThreshold) {
          return false; // We're past the section, show sticky bar
        }
        
        // If section bottom is above the threshold, we've scrolled through enough
        // This makes sticky appear when entering the next section
        if (sectionBottom < bottomThreshold) {
          return false; // We're past the active animation range, show sticky bar
        }
        
        // Section is still in active animation range - keep sticky hidden
        return true;
      }
      
      // For normal-height sections, use standard visibility check with margins
      return isElementVisible(el, {
        topOffset: SCROLL_SECTION_HIDE_MARGIN_PX,
        bottomOffset: SCROLL_SECTION_HIDE_MARGIN_PX,
      });
    };

    const mainCtaVisible = isElementVisible(this.mainAddToCartButton);
    
    // Check if we've scrolled past the main CTA button
    // This ensures sticky only shows AFTER scrolling past the main CTA, not at the beginning
    const hasScrolledPastMainCta = hasScrolledPastElement(this.mainAddToCartButton);
    
    // Primary behavior: show whenever the main CTA is not visible AND we've scrolled past it.
    // This prevents showing at the beginning when main CTA is below the fold on mobile.
    // Fallback: if CTA button isn't found, use threshold to avoid never showing.
    const canShowAfterCta = this.mainAddToCartButton 
      ? (!mainCtaVisible && hasScrolledPastMainCta) 
      : pastThreshold;

    let shouldShow = canShowAfterCta;

    // Check if scroll-image-transition, image-sequence-canvas, or image-sequence-video sections are visible
    // Hide sticky add to cart when these scrollytelling sections are actively animating
    const scrollImageTransition = document.querySelector('.scroll-image-transition');
    const imageSequenceCanvas = document.querySelector('.image-sequence-canvas');
    const imageSequenceVideo = document.querySelector('.image-sequence-video');
    let isScrollSectionVisible = false;

    if (scrollImageTransition || imageSequenceCanvas || imageSequenceVideo) {
      isScrollSectionVisible =
        isScrollSectionActive(scrollImageTransition) || 
        isScrollSectionActive(imageSequenceCanvas) || 
        isScrollSectionActive(imageSequenceVideo);
    }

    // Check if we're near the footer (optional - hide bar when footer is visible)
    const footer = document.querySelector('footer');
    const footerRect = footer?.getBoundingClientRect();
    const isNearFooter = footerRect && footerRect.top < window.innerHeight;

    if (shouldShow && !isNearFooter && !isScrollSectionVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  /**
   * Show the sticky bar
   */
  show() {
    const stickyBar = this.querySelector('.sticky-add-to-cart');
    if (stickyBar) {
      stickyBar.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Hide the sticky bar
   */
  hide() {
    const stickyBar = this.querySelector('.sticky-add-to-cart');
    if (stickyBar) {
      stickyBar.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Handle variant changes from the main product form
   * @param {CustomEvent} event - The variant change event
   */
  handleVariantChange(event) {
    const { variant, price } = event.detail || {};

    if (!variant) return;

    // Update the hidden variant input in the sticky form
    /** @type {HTMLInputElement | null} */
    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update the selling plan input if present
    /** @type {HTMLInputElement | null} */
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput && variant.selling_plan_allocation) {
      sellingPlanInput.value = variant.selling_plan_allocation.selling_plan.id;
    }

    // Update the button text with new price
    const buttonText = this.querySelector('.add-to-cart-text__content');
    if (buttonText && price) {
      const buttonLabel = buttonText.textContent.split(' - ')[0];
      buttonText.textContent = `${buttonLabel} - ${price}`;
    }

    // Update availability
    const addToCartButton = this.querySelector('button[type="submit"]');
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.removeAttribute('disabled');
      } else {
        addToCartButton.setAttribute('disabled', 'true');
      }
    }
  }

  /**
   * Scroll to the main product form when "Update" is clicked
   */
  scrollToForm() {
    const mainForm = document.querySelector('.product-information');
    if (mainForm) {
      const headerOffset = 100; // Adjust based on your header height
      const elementPosition = mainForm.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }
}

customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);

